// Tiny XML exporter — no dependency. Builds a simple, well-formed XML document
// (root element with one <record> per row, each column as an attribute) and
// triggers a browser download. Intended as a generic, portable export the user
// can transform into an operadora's format later — NOT the full TISS schema.

export interface XmlColumn<T> {
    /** Attribute name (must be a valid XML name: letters/digits/_/-, no spaces). */
    attr: string;
    value: (row: T) => string | number | null | undefined;
}

const escapeAttr = (v: string | number | null | undefined): string => {
    const s = v == null ? '' : String(v);
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
};

export function exportXml<T>(
    filename: string,
    rootTag: string,
    recordTag: string,
    columns: XmlColumn<T>[],
    rows: T[],
    rootAttrs: Record<string, string | number> = {}
): void {
    const rootAttrStr = Object.entries(rootAttrs)
        .map(([k, v]) => ` ${k}="${escapeAttr(v)}"`)
        .join('');
    const body = rows
        .map((r) => {
            const attrs = columns.map((c) => `${c.attr}="${escapeAttr(c.value(r))}"`).join(' ');
            return `  <${recordTag} ${attrs}/>`;
        })
        .join('\n');
    const xml =
        `<?xml version="1.0" encoding="UTF-8"?>\n` +
        `<${rootTag}${rootAttrStr}>\n${body}\n</${rootTag}>\n`;
    const blob = new Blob([xml], { type: 'application/xml;charset=utf-8;' });
    triggerDownload(blob, filename.endsWith('.xml') ? filename : `${filename}.xml`);
}

function triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
